import { useEffect, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { API_BASE_URL, AuthError, apiFetch } from '@/api/client'
import { useAuth } from '@/hooks/use-auth'

/**
 * A file from a staff-only route (it needs the staff token, so an <img src> can't load it) as an object
 * URL for an image, a New tab link or a download. ``missing`` and ``failed`` are what staff are told.
 */
export function useBlobUrl(queryKey: readonly unknown[], path: string, missing: string, failed: string) {
  const { getAccessToken } = useAuth()
  const query = useQuery({
    queryKey,
    queryFn: async ({ signal }) => {
      const token = await getAccessToken()
      if (!token) throw new AuthError(401, 'Failed to acquire access token')
      const response = await apiFetch(`${API_BASE_URL}${path}`, {
        headers: { Authorization: `Bearer ${token}` },
        signal,
      })
      if (!response.ok) throw new Error(response.status === 404 ? missing : failed)
      return response.blob()
    },
    staleTime: Infinity,
  })
  const url = useMemo(() => (query.data ? URL.createObjectURL(query.data) : null), [query.data])
  useEffect(
    () => () => {
      if (url) URL.revokeObjectURL(url)
    },
    [url],
  )
  return { url, error: query.error, isLoading: query.isLoading, refetch: query.refetch }
}
